import { useState } from 'react'
import { Button, ActionIcon, Tooltip, TextInput, Modal } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconFingerprint, IconTrash, IconPlus } from '@tabler/icons-react'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser'

export default function ProfileManagePasskeys({ passkeys = [], onMutate }) {
    const { requestGet, requestPost, requestDelete } = useAxiosApi()
    const [registerLoading, setRegisterLoading] = useState(false)
    const [error, setError] = useState(null)

    // Device name modal
    const [nameModalOpened, nameModalHandlers] = useDisclosure(false)
    const [deviceName, setDeviceName] = useState('')
    const [pendingAttestation, setPendingAttestation] = useState(null)

    const handleRegister = async () => {
        setRegisterLoading(true)
        setError(null)

        try {
            // 1. Get registration options
            const { data: optionsJSON } = await requestGet(
                '/webauthn/register-options'
            )

            // 2. Start WebAuthn registration (browser prompt)
            const attestation = await startRegistration({ optionsJSON })

            // 3. Ask for device name
            setPendingAttestation(attestation)
            setDeviceName('')
            nameModalHandlers.open()
        } catch (err) {
            if (err.name !== 'NotAllowedError') {
                setError(err.message || "Erreur lors de l'enregistrement")
            }
        } finally {
            setRegisterLoading(false)
        }
    }

    const handleSavePasskey = async () => {
        if (!pendingAttestation) return

        setRegisterLoading(true)
        try {
            await requestPost('/webauthn/register', {
                ...pendingAttestation,
                device_name: deviceName || null,
            })

            nameModalHandlers.close()
            setPendingAttestation(null)
            onMutate?.()
        } catch (err) {
            setError(
                err.response?.data?.error_message ||
                    "Erreur lors de l'enregistrement"
            )
        } finally {
            setRegisterLoading(false)
        }
    }

    const handleDelete = async (id) => {
        try {
            await requestDelete(`/webauthn/passkeys/${id}`)
            onMutate?.()
        } catch (err) {
            setError(
                err.response?.data?.error_message ||
                    'Erreur lors de la suppression'
            )
        }
    }

    if (!browserSupportsWebAuthn()) {
        return null
    }

    return (
        <>
            <Modal
                opened={nameModalOpened}
                onClose={() => {
                    nameModalHandlers.close()
                    setPendingAttestation(null)
                }}
                title="Nommer cet appareil"
                size="sm"
            >
                <div className="flex flex-col gap-3">
                    <TextInput
                        label="Nom de l'appareil"
                        placeholder="ex: MacBook Pro, iPhone..."
                        value={deviceName}
                        onChange={(e) => setDeviceName(e.target.value)}
                        autoFocus
                    />
                    <Button
                        onClick={handleSavePasskey}
                        loading={registerLoading}
                    >
                        Enregistrer
                    </Button>
                </div>
            </Modal>

            <div className="flex flex-col gap-2">
                {passkeys.length > 0 && (
                    <div className="flex flex-col gap-1 border rounded-lg divide-y">
                        {passkeys.map((passkey) => (
                            <div
                                key={passkey.id}
                                className="flex items-center justify-between px-3 py-2"
                            >
                                <div className="flex items-center gap-2">
                                    <IconFingerprint
                                        size={16}
                                        className="text-gray-400"
                                    />
                                    <div>
                                        <span className="text-sm">
                                            {passkey.device_name || 'Appareil'}
                                        </span>
                                        <span className="text-xs text-gray-400 ml-2">
                                            {new Date(
                                                passkey.created_at
                                            ).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                                <Tooltip label="Supprimer">
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        size="sm"
                                        onClick={() =>
                                            handleDelete(passkey.id)
                                        }
                                    >
                                        <IconTrash size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                )}

                <Button
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    onClick={handleRegister}
                    loading={registerLoading}
                >
                    Ajouter un appareil
                </Button>

                {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                )}
            </div>
        </>
    )
}
